import { API_BASE, getAccessToken, http } from './api';

export interface Campaign {
  id: string;
  name: string;
  description?: string | null;
  status: string;
  companies_targeted?: number;
  total_prospects?: number;
  websites_count?: number;
  processed?: number;
  processed_count?: number;
  success?: number;
  success_count?: number;
  failed?: number;
  failed_count?: number;
  replied_count?: number;
  interested_count?: number;
  created_at?: string;
  updated_at?: string;
  createdAt?: string;
  updatedAt?: string;
  websites?: string[];
}

export interface CampaignDetail extends Campaign {
  performance: CampaignPerformance;
  audienceCount: number;
}

/** The ACTUAL shape returned by GET /campaigns/{id} (backend CampaignResponse) — a contact-form
 *  campaign with real submission progress. The legacy camelCase CampaignDetail above was a fiction
 *  (the page read performance/audienceCount/createdAt that the backend never sends → fake zeros +
 *  "Invalid Date"). Use this for the detail view. */
export interface CampaignDetailResponse {
  id: string;
  user_id: string;
  name: string;
  status: 'draft' | 'active' | 'paused' | 'completed' | string;
  contact_name: string;
  contact_email: string;
  websites_count: number;
  processed_count: number;
  success_count: number;
  failed_count: number;
  websites: string[];
  automation_settings: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  started_at?: string | null;
  completed_at?: string | null;
  celery_task_id?: string | null;
}

export interface CampaignStep {
  id: string;
  type: 'email' | 'linkedin' | 'call' | 'form';
  template?: string;
  delay: number; // hours
  order: number;
  enabled: boolean;
}

export interface CampaignPerformance {
  totalSent: number;
  totalOpens: number;
  totalClicks: number;
  totalResponses: number;
  openRate: number;
  clickRate: number;
  responseRate: number;
  conversionRate: number;
  series: PerformanceDataPoint[];
}

export interface PerformanceDataPoint {
  date: string;
  sent?: number;
  opens?: number;
  clicks?: number;
  responses?: number;
}

// Legacy interface - used by some older components
export interface CampaignCreate {
  name: string;
  description?: string;
  audienceFilters: FilterOptions;
  steps: Omit<CampaignStep, 'id'>[];
  startAt?: string;
}

// Contact-form campaign — matches server/src/api/schemas/campaign.py exactly.
// LinkedIn outreach uses a different execution substrate and must not be sent here.
export interface CreateCampaignInput {
  name: string;
  description?: string | null;
  contact_info: {
    name: string;
    email: string;
    phone?: string | null;
    company: string;
    title?: string | null;
    country?: string | null;
  };
  message_settings: {
    subject: string;
    message: string;
    use_personalization: boolean;
    personalization_prompt?: string | null;
  };
  automation_settings?: {
    delay_between_submissions: number;
    max_submissions_per_hour: number;
    respect_robots_txt: boolean;
    max_retries: number;
  };
  llm_settings?: {
    llm_model_type: string;
    llm_model: string;
    llm_temperature: number;
    use_llm_cache: boolean;
  };
  browser_settings?: {
    headless_mode: boolean;
    user_agent?: string | null;
    proxy_url?: string | null;
  };
  websites: string[];
}

export interface CampaignsResponse {
  items: Campaign[];
  total: number;
  page: number;
  page_size: number;
}

export interface FilterOptions {
  search?: string;
  industry?: string[];
  companySize?: string[];
  status?: string[];
  location?: string[];
  tags?: string[];
  leadScore?: [number, number];
}

export interface WebsiteImportResponse {
  websites: string[];
  total_rows: number;
  websites_found: number;
  skipped_rows: number;
  detected_column: string | null;
  warnings: string[];
}

class CampaignService {
  private campaignsBaseUrl = '/campaigns';
  private createUrl = '/campaigns/create';

  async getCampaigns(
    page = 1,
    pageSize = 20,
    search?: string,
    status?: string
  ): Promise<CampaignsResponse> {
    const params = new URLSearchParams({
      skip: ((page - 1) * pageSize).toString(),
      limit: pageSize.toString(),
      ...(search && { search }),
      ...(status && { status })
    });

    const data = await http<{ success?: boolean; data?: { items: Campaign[]; total: number; page: number; page_size: number } }>(`${this.campaignsBaseUrl}/`, {
      method: 'GET',
      query: Object.fromEntries(params)
    });
    
    // Backend returns: { success: true, data: { items, total, page, page_size } }
    // Transform to match CampaignsResponse interface
    if (data.success && data.data) {
      return {
        items: data.data.items || [],
        total: data.data.total || 0,
        page: data.data.page || page,
        page_size: data.data.page_size || pageSize
      };
    }
    
    // Fallback for direct response format - extract fields from data
    return {
      items: (data as unknown as CampaignsResponse).items || [],
      total: (data as unknown as CampaignsResponse).total || 0,
      page: (data as unknown as CampaignsResponse).page || page,
      page_size: (data as unknown as CampaignsResponse).page_size || pageSize
    };
  }

  async getCampaign(id: string): Promise<CampaignDetailResponse> {
    return http<CampaignDetailResponse>(`${this.campaignsBaseUrl}/${id}`);
  }

  async createCampaign(campaign: CreateCampaignInput): Promise<Campaign> {
    const data = await http<{ success?: boolean; data?: Campaign } & Campaign>(this.createUrl, {
      method: 'POST',
      body: JSON.stringify(campaign),
    });

    // Backend returns: { success: true, data: Campaign }
    if (data.success && data.data) {
      return data.data;
    }

    // Fallback for direct response
    return data;
  }

  async updateCampaign(id: string, updates: Partial<Campaign>): Promise<Campaign> {
    return http<Campaign>(`${this.campaignsBaseUrl}/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async getCampaignPerformance(
    id: string,
    range: '7d' | '30d' | '90d' = '30d'
  ): Promise<CampaignPerformance> {
    return http<CampaignPerformance>(`${this.campaignsBaseUrl}/${id}/performance`, {
      query: { range },
    });
  }

  async startCampaign(id: string): Promise<{ success: boolean }> {
    return http<{ success: boolean }>(`${this.campaignsBaseUrl}/${id}/start`, {
      method: 'POST',
    });
  }

  async pauseCampaign(id: string): Promise<{ success: boolean }> {
    return http<{ success: boolean }>(`${this.campaignsBaseUrl}/${id}/pause`, {
      method: 'POST',
    });
  }

  /**
   * Import website URLs from a CSV or Excel file
   * @param file - CSV (.csv) or Excel (.xlsx) file
   * @param websiteColumn - Optional: specify which column contains websites
   * @returns List of extracted website URLs
   */
  async importWebsitesFromFile(
    file: File,
    websiteColumn?: string
  ): Promise<WebsiteImportResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const params = new URLSearchParams();
    if (websiteColumn) {
      params.append('website_column', websiteColumn);
    }

    const queryString = params.toString();
    const url = `${this.campaignsBaseUrl}/import-websites${queryString ? `?${queryString}` : ''}`;

    // Use fetch directly for FormData (http wrapper may not handle it correctly)
    const token = getAccessToken();
    const response = await fetch(`${API_BASE}${url}`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(errorData.detail || 'Failed to import websites');
    }

    return response.json();
  }
}

export const campaignService = new CampaignService();
